import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Heart, Target, Users, Calendar, ArrowRight, ArrowLeft,
  DollarSign, TrendingUp, Share2, ChevronRight
} from "lucide-react";
import { format } from "date-fns";

// Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/koo5s91n_BluevsBlue1%20%281%29.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/aqfim3pc_5143063002655456367.jpeg";

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [featuredCampaign, setFeaturedCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns?active_only=true`);
      const allCampaigns = response.data;
      
      // Find featured campaign
      const featured = allCampaigns.find(c => c.is_featured);
      if (featured) {
        setFeaturedCampaign(featured);
        setCampaigns(allCampaigns.filter(c => c.campaign_id !== featured.campaign_id));
      } else {
        setCampaigns(allCampaigns);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const getProgress = (raised, goal) => {
    if (!goal || goal === 0) return 0;
    return Math.min((raised / goal) * 100, 100);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleShare = async (campaign) => {
    const url = `${window.location.origin}/campaigns/${campaign.campaign_id}`;
    const text = `Support "${campaign.title}" - ${campaign.description?.slice(0, 100)}...`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: campaign.title, text, url });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Campaign link copied to clipboard!"))
      .catch(() => toast.error("Clipboard unavailable — copy manually"));
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-deep-blue" data-testid="campaigns-hero">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={HERO_IMAGE} 
            alt="Campaigns background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative container-custom text-center">
          <span className="inline-flex items-center gap-2 bg-awareness-blue/20 text-awareness-blue px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Target className="w-4 h-4" />
            Active Fundraising Campaigns
          </span>
          <h1 className="text-heading-1 text-white mb-4">Support Our Campaigns</h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Choose a campaign that resonates with you and help us make a difference in the fight against prostate cancer.
          </p>
        </div>
      </section>

      {/* Featured Campaign */}
      {featuredCampaign && (
        <section className="section-spacing bg-gradient-to-b from-slate-50 to-white" data-testid="featured-campaign">
          <div className="container-custom">
            <div className="text-center mb-8">
              <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide flex items-center justify-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Featured Campaign
              </span>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-100">
              <div className="grid md:grid-cols-2">
                <div className="relative h-64 md:h-auto">
                  <img 
                    src={featuredCampaign.image_url || HERO_IMAGE}
                    alt={featuredCampaign.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-awareness-blue text-deep-blue px-3 py-1 rounded-full text-xs font-bold">
                      FEATURED
                    </span>
                  </div>
                </div>
                
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <h2 className="text-2xl md:text-3xl font-bold text-deep-blue mb-4">
                    {featuredCampaign.title}
                  </h2>
                  <p className="text-slate-600 mb-6 line-clamp-3">
                    {featuredCampaign.description}
                  </p>
                  
                  {/* Progress */}
                  <div className="mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-bold text-deep-blue text-xl">
                        {formatCurrency(featuredCampaign.raised)}
                      </span>
                      <span className="text-slate-500">
                        Goal: {formatCurrency(featuredCampaign.goal)}
                      </span>
                    </div>
                    <Progress 
                      value={getProgress(featuredCampaign.raised, featuredCampaign.goal)} 
                      className="h-3 bg-slate-200"
                    />
                    <div className="flex justify-between text-sm mt-2 text-slate-500">
                      <span>{Math.round(getProgress(featuredCampaign.raised, featuredCampaign.goal))}% funded</span>
                      <span>{featuredCampaign.donors_count || 0} donors</span>
                    </div>
                  </div>
                  
                  {/* End date if exists */}
                  {featuredCampaign.end_date && (
                    <p className="text-sm text-slate-500 mb-6 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Ends {format(new Date(featuredCampaign.end_date), "MMMM d, yyyy")}
                    </p>
                  )}
                  
                  <div className="flex flex-wrap gap-3">
                    <Link to={`/donate?campaign=${featuredCampaign.campaign_id}`} className="flex-1">
                      <Button className="w-full btn-primary text-base py-3" data-testid="featured-donate-btn">
                        <Heart className="w-5 h-5 mr-2" />
                        Donate Now
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      className="border-slate-300"
                      onClick={() => handleShare(featuredCampaign)}
                      data-testid="featured-share-btn"
                    >
                      <Share2 className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All Campaigns Grid */}
      <section className="section-spacing" data-testid="campaigns-grid">
        <div className="container-custom">
          {campaigns.length > 0 && (
            <>
              <div className="text-center mb-12">
                <h2 className="text-heading-2 text-deep-blue">All Campaigns</h2>
                <p className="text-slate-600 mt-2">
                  Every donation makes a difference. Choose a cause close to your heart.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {campaigns.map((campaign) => (
                  <CampaignCard 
                    key={campaign.campaign_id} 
                    campaign={campaign}
                    onShare={handleShare}
                  />
                ))}
              </div>
            </>
          )}
          
          {!loading && campaigns.length === 0 && !featuredCampaign && (
            <div className="text-center py-16">
              <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-600 mb-2">No Active Campaigns</h3>
              <p className="text-slate-500 mb-6">
                Check back soon for new fundraising campaigns!
              </p>
              <Link to="/donate">
                <Button className="btn-primary">
                  Make a General Donation
                </Button>
              </Link>
            </div>
          )}
          
          {loading && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse">
                  <div className="h-48 bg-slate-200" />
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-slate-200 rounded w-3/4" />
                    <div className="h-4 bg-slate-200 rounded w-full" />
                    <div className="h-4 bg-slate-200 rounded w-5/6" />
                    <div className="h-3 bg-slate-200 rounded-full w-full" />
                    <div className="h-10 bg-slate-200 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* General Donation CTA */}
      <section className="py-16 bg-deep-blue" data-testid="general-donation-cta">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Want to support our general fund?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Your unrestricted donation goes where it's needed most, helping us fund critical programs and operations.
          </p>
          <Link to="/donate">
            <Button className="bg-awareness-blue text-deep-blue hover:bg-[#5aa0d1] font-bold text-lg px-8 py-3" data-testid="general-donate-btn">
              Make a General Donation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
}

function CampaignCard({ campaign, onShare }) {
  const getProgress = (raised, goal) => {
    if (!goal || goal === 0) return 0;
    return Math.min((raised / goal) * 100, 100);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow group"
      data-testid={`campaign-card-${campaign.campaign_id}`}
    >
      <div className="relative h-48 overflow-hidden">
        <img 
          src={campaign.media?.[0]?.url || campaign.image_url || HERO_IMAGE}
          alt={campaign.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <span className="text-white/90 text-sm font-medium">
            {campaign.donors_count || 0} donors
          </span>
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="font-bold text-lg text-deep-blue mb-2 line-clamp-1">
          {campaign.title}
        </h3>
        <p className="text-slate-600 text-sm mb-4 line-clamp-2">
          {campaign.description}
        </p>
        
        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="font-bold text-deep-blue">
              {formatCurrency(campaign.raised)}
            </span>
            <span className="text-slate-500 text-xs">
              of {formatCurrency(campaign.goal)}
            </span>
          </div>
          <Progress 
            value={getProgress(campaign.raised, campaign.goal)} 
            className="h-2 bg-slate-200"
          />
          <div className="text-right text-xs text-slate-500 mt-1">
            {Math.round(getProgress(campaign.raised, campaign.goal))}% funded
          </div>
        </div>
        
        {/* End date */}
        {campaign.end_date && (
          <p className="text-xs text-slate-500 mb-4 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Ends {format(new Date(campaign.end_date), "MMM d, yyyy")}
          </p>
        )}
        
        <div className="flex gap-2">
          <Link to={`/donate?campaign=${campaign.campaign_id}`} className="flex-1">
            <Button className="w-full btn-primary text-sm" data-testid={`donate-${campaign.campaign_id}`}>
              <Heart className="w-4 h-4 mr-1" />
              Donate
            </Button>
          </Link>
          <Button 
            variant="outline" 
            size="icon"
            className="border-slate-300"
            onClick={() => onShare(campaign)}
          >
            <Share2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
